import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { Button } from '@pesto/ui/components/Button';
import { SmartForm } from '@pesto/ui/features/SmartForm/SmartForm';
import { RocketIcon } from 'lucide-react';
import { z } from 'zod';

import type { ModalProps } from '../../types/modal.types';

import { type CreateUserMutationMutation, useCreateUserMutationMutation } from '../../../__generated__/graphql/api';
import { IS_PROD } from '../../../constants/global-env';
import { useRandomUserGenerator } from '../../../pages/Users/hooks/useRandomUserGenerator';
import { ApplicantsSection } from './ApplicantsSection';
import { applicantSchema, emptyApplicant, toUserInput } from './openAccountSchema';

const createUserSchema = z.object({ applicants: z.array(applicantSchema).length(1) });

type CreateUserFormData = z.infer<typeof createUserSchema>;

const formConfig: SmartFormConfig<CreateUserFormData> = {
  fields: [],
  schema: createUserSchema,
  defaultValues: { applicants: [emptyApplicant] },
};

export const CreateUserModalComponent = ({
  api,
}: ModalProps<undefined, { user: CreateUserMutationMutation['createUser'] }>) => {
  const { handleMutation } = useMutationWithToast();
  const [createUser, { loading }] = useCreateUserMutationMutation();
  const { generateSampleApplicant } = useRandomUserGenerator();

  const handleSubmit = async (data: CreateUserFormData) => {
    await handleMutation({
      mutation: () => createUser({ variables: { user: toUserInput(data.applicants[0]) } }),
      extractSuccess: (result) => !!result?.data?.createUser?.id,
      onSuccess: (result) => api.close({ user: result?.data?.createUser }),
      debugLabel: 'create-user-modal',
      toast: {
        success: 'Person created',
        error: 'Failed to create person',
        loading: 'Creating person...',
      },
    });
  };

  return (
    <SmartForm config={formConfig} onSubmit={handleSubmit} loading={loading}>
      {(form) => (
        <div className="flex flex-col gap-4">
          {!IS_PROD && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => form.setValue('applicants', [generateSampleApplicant()])}
              >
                <RocketIcon className="h-4 w-4" /> Sample Data
              </Button>
            </div>
          )}

          <ApplicantsSection control={form.control} maxApplicants={1} hideCardFields hideSsn />
        </div>
      )}
    </SmartForm>
  );
};
